/**
 * One message.
 *
 * **The body is text.** `{message.body}` and nothing else — React escapes a
 * string child, so this is safe by construction, and `dangerouslySetInnerHTML`
 * is the only way to break it. A quote's author and excerpt and an
 * attachment's name are the same: sender-supplied, display only, drawn as
 * text.
 *
 * `lead` is the grouping. Consecutive messages from one author inside ten
 * minutes share a header and sit 4px apart; past the window, across midnight
 * or from another author, the avatar and name return with the full lead.
 * `Transcript` decides it; this row only draws it.
 *
 * The hover bar is the desktop's: three quick reactions, Reply — which quotes
 * the message into the composer — and More, which opens the message's menu.
 * One reply glyph, not two: replying in a thread has its own two ways in, the
 * menu and the row under a message that already has replies. A right-click
 * opens the same menu.
 *
 * Edit opens the body in place (`MessageEditor`), and the hover bar steps
 * aside until it closes.
 *
 * An attachment is drawn in place where it can be (`AttachmentPreview`), and
 * is the card that opens it in Cloud where it cannot.
 *
 * A message may be drawn translated. In the automatic mode every message that
 * is not the reader's own asks for its translation as the row mounts; in the
 * on-demand mode the menu's Translate does. Either way the drawn body is text
 * like any other, and the only mark it happened is a small faint one trailing
 * the last line — the language DeepL detected, which is the reader's way to
 * tell a translation from the sender's own words, and the button back to the
 * original. It is inline on purpose: a chat row has no room for a second line
 * under every message.
 */

import { Fragment, useEffect, useState, type MouseEvent } from 'react';
import { ds } from '../../ds';
import { useI18n, useT } from '../../i18n';
import { formatTime, relativeTime } from '../../i18n/format';
import { autoLoads } from '../../lib/relay/preview';
import { store } from '../../lib/relay/store';
import {
  MAX_TEXT_CHARS, translations, useTranslation, useTranslationState
} from '../../lib/relay/translate';
import { Avatar } from '../Avatar';
import { AttachmentView } from './AttachmentPreview';
import { MessageEditor } from './MessageEditor';
import { MessageMenu } from './MessageMenu';
import { mentionSegments } from './mention';
import { messageMenu, type MenuEntry } from './menu';
import { VerifiedMark } from './Official';
import type { Attachment, Message, Person, ThreadSummary } from '../../lib/relay/types';
import './chat.css';

const QUICK = ['\u{1F44D}', '\u{2764}', '\u{1F602}'];

function AttachmentCard({ file }: { file: Attachment }) {
  const { Icon } = ds();
  const t = useT();

  return (
    <a
      href={file.cloudUrl}
      target="_blank"
      rel="noreferrer"
      title={t('conversation.attachment.open')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-8)',
        marginTop: 'var(--space-8)',
        padding: 'var(--space-8) var(--space-12)',
        borderRadius: 'var(--radius-md, 10px)',
        background: 'var(--app-panel-sunken)',
        textDecoration: 'none',
        color: 'inherit'
      }}
    >
      <Icon name="Attach" size={16} />
      <span style={{ minWidth: 0 }}>
        <span className="rl-truncate" style={{ display: 'block', fontWeight: 500 }}>
          {file.name}
        </span>
        <span
          style={{
            color: 'var(--text-faint)',
            fontFamily: 'var(--font-mono), monospace',
            fontSize: 11
          }}
        >
          {file.mime} · {Math.round(file.bytes / 1024)} kB
        </span>
      </span>
    </a>
  );
}

function Reactions({ message, meId, readOnly }: { message: Message; meId?: string; readOnly?: boolean }) {
  const reactions = message.reactions ?? [];
  if (reactions.length === 0) return null;

  return (
    <div className="rl-reactions">
      {reactions.map((r) => {
        const mine = meId !== undefined && r.userIds.includes(meId);
        return (
          <button
            key={r.emoji}
            type="button"
            className="rl-reaction"
            aria-pressed={mine}
            disabled={readOnly}
            onClick={() => store.react?.(message.conversationId, message.id, r.emoji, !mine)}
          >
            <span>{r.emoji}</span>
            <span>{r.userIds.length}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The row under a thread's root: who replied, how many, and when last. The
 * whole row opens the thread.
 *
 * The faces are the first three to reply, as the desktop draws them. Anybody
 * the page cannot name — somebody who has since left — is drawn from their id.
 */
function ThreadRow({
  summary,
  faces,
  onOpen
}: {
  summary: ThreadSummary;
  faces: Person[];
  onOpen: () => void;
}) {
  const t = useT();
  const { tn } = useI18n();
  const byId = new Map(faces.map((p) => [p.id, p]));

  return (
    <button type="button" className="rl-threadrow" onClick={onOpen}>
      <span className="rl-stack">
        {summary.participants.map((id) => (
          <Avatar key={id} person={byId.get(id) ?? { id, name: '' }} size={18} />
        ))}
      </span>
      <span className="rl-threadrow-count">
        {tn('conversation.thread.replies', summary.replyCount)}
      </span>
      <span className="rl-threadrow-last">
        {t('conversation.thread.lastReply', {
          when: relativeTime(
            Math.floor(summary.lastAt / 1000),
            t('conversation.thread.justNow')
          )
        })}
      </span>
    </button>
  );
}

export function MessageRow({
  message,
  author,
  lead,
  meId,
  direct,
  verified,
  readOnly,
  faces,
  onReply,
  onThread
}: {
  message: Message;
  author?: Person;
  lead: boolean;
  meId?: string;
  /** Mauvely wrote this, in Mauvely's own conversation: `isOfficialMessage`,
   *  from the server's flag and stamp. Never from `author.name`. */
  verified?: boolean;
  /** Nothing here can be answered: no reactions, no reply, and a menu that
   *  only copies. */
  readOnly?: boolean;
  /** Whether this is a two-person conversation. The only thing it decides is
   *  whether "not delivered" needs to say to how many: in a DM there is one
   *  other person and a count reads as arithmetic, and in a group the message
   *  did reach everybody else, so an unqualified "Not delivered" is wrong. */
  direct?: boolean;
  /** Who to draw the thread row's faces, and the mentions' names, from. */
  faces?: Person[];
  /** Quote this message in the composer — the desktop's Reply. */
  onReply: (message: Message) => void;
  /** Open this message's thread. Absent inside the thread pane, where a row
   *  does not start another thread and draws no thread row. */
  onThread?: (message: Message) => void;
}) {
  const { Icon, IconButton } = ds();
  const { t, tn } = useI18n();
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [editing, setEditing] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const mine = meId !== undefined && message.senderId === meId;
  const prefs = useTranslationState();
  const translation = useTranslation(message);
  const target = prefs?.available === true ? translations.target() : '';
  const autoTranslate =
    prefs?.available === true && prefs.mode === 'auto' && !mine && message.kind === 'msg';
  // The menu offers a translation only where one is makeable and nothing is
  // already drawn: after `same` (already in the target) or a translation, the
  // entry would be a click that does nothing.
  const translatable =
    prefs?.available === true &&
    message.kind === 'msg' &&
    message.body.trim() !== '' &&
    message.body.length <= MAX_TEXT_CHARS &&
    (translation.status === 'idle' || translation.status === 'failed');
  const entries = messageMenu({
    mine,
    inThread: !onThread,
    readOnly: readOnly === true,
    ...(translatable ? { translatable: true } : {})
  });
  const auto = autoLoads(message.attachments);

  // The automatic pass. It re-runs when the message changed (an edit) or the
  // target did (a different language chosen, or "my language" resolving once
  // the picker's list arrived), because both make a held translation stale;
  // `request` itself asks nothing that is already held, queued or in flight.
  useEffect(() => {
    if (autoTranslate) translations.request(message);
  }, [autoTranslate, message.id, message.body, target]);

  const pick = (entry: MenuEntry) => {
    setMenu(null);
    switch (entry.kind) {
      case 'react': {
        const on = !(message.reactions ?? []).some(
          (r) => r.emoji === entry.emoji && meId !== undefined && r.userIds.includes(meId)
        );
        store.react?.(message.conversationId, message.id, entry.emoji, on);
        return;
      }
      case 'reply':
        return onReply(message);
      case 'thread':
        return onThread?.(message);
      case 'copy':
        void navigator.clipboard?.writeText(message.body).catch(() => {});
        return;
      case 'translate':
        // A person's action, so it also clears a failure the page is holding.
        return translations.retry(message);
      case 'edit':
        return setEditing(true);
      case 'delete':
        return store.deleteMessage(message.conversationId, message.id);
    }
  };

  // A right-click, or the keyboard's menu key, which arrives as the same event
  // with no pointer position: then the menu opens at the row's corner.
  const onContextMenu = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const box = e.currentTarget.getBoundingClientRect();
    const keyboard = e.clientX === 0 && e.clientY === 0;
    setMenu(keyboard ? { x: box.right - 220, y: box.top } : { x: e.clientX, y: e.clientY });
  };

  const openFrom = (e: MouseEvent) => {
    const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenu({ x: box.right - 220, y: box.bottom + 4 });
  };

  return (
    <div className={lead ? 'rl-msg rl-msg-lead' : 'rl-msg'} onContextMenu={onContextMenu}>
      {/* ── No bubbles, and no side ─────────────────────────────────────────
          The app draws every message the same way — a face, a name, a time and
          the words, left to right — whoever sent it. A transcript of bubbles
          leaning right is a different product, and this one is supposed to be
          the same product in a browser.

          So this row does not take `own` at all. It took it to flip the row
          into `row-reverse`, which outlived the bubbles by one commit: the
          fill went and the side stayed, and your own messages still ran the
          other way. A call row keeps its own `own` — "you called them" and
          "they called you" are different sentences. */}
      {lead && author ? (
        <Avatar person={author} size={32} />
      ) : (
        <span style={{ width: 32, flex: '0 0 auto' }} aria-hidden="true" />
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        {lead && author && (
          <div className="rl-msg-line">
            <span className="rl-msg-name">
              {author.name}
              {verified && <VerifiedMark size={13} />}
            </span>
            <span className="rl-msg-time">{formatTime(message.at)}</span>
            {message.pending === 'sending' && (
              <span className="rl-msg-time">{t('conversation.pending')}</span>
            )}
            {message.pending === 'failed' && (
              <span className="rl-msg-failed">{t('conversation.failed')}</span>
            )}
            {/* Sent, acked, and it reached nobody on the other side of an
                organisation boundary. Distinct from `failed`, which invites a
                retry — this one will not change however many times it is sent,
                and saying "Not sent" would be a lie about what happened. */}
            {message.undeliveredTo && (
              <span className="rl-msg-failed">
                {direct
                  ? t('conversation.undelivered')
                  : tn('conversation.undeliveredTo', message.undeliveredTo.length)}
              </span>
            )}
          </div>
        )}

        {message.quote && (
          <div className="rl-quote">
            <div className="rl-quote-author">{message.quote.author}</div>
            <div>{message.quote.excerpt}</div>
          </div>
        )}

        {/* Text. See this file's header. A pill only where an id the message
            carries meets its own @Name; the words alone are never a mention.
            A translation is drawn through the same segments, so a name that
            survived translation keeps its pill and one that did not is only
            words — the same rule the original body is held to. The trailing
            mark is inside the body on purpose: it rides the last line, so a
            one-line message stays one line. */}
        {editing ? (
          <MessageEditor message={message} onClose={() => setEditing(false)} />
        ) : (
          <div className="rl-msg-body">
            {mentionSegments(
              translation.status === 'translated' && !showOriginal ? translation.text : message.body,
              message.mentions,
              faces ?? []
            ).map((part, i) =>
              typeof part === 'string' ? (
                <Fragment key={i}>{part}</Fragment>
              ) : (
                <span key={i} className={part.id === meId ? 'rl-mention rl-mention-me' : 'rl-mention'}>
                  {part.text}
                </span>
              )
            )}
            {translation.status === 'loading' && (
              <span className="rl-translation-mark">{t('conversation.translation.loading')}</span>
            )}
            {translation.status === 'translated' && (
              <button
                type="button"
                className="rl-translation-mark"
                title={t('conversation.translation.from', { language: translation.languageName })}
                aria-label={
                  showOriginal
                    ? t('conversation.translation.show')
                    : t('conversation.translation.original')
                }
                onClick={() => setShowOriginal(!showOriginal)}
              >
                <Icon name="Translate" size={11} />
                {showOriginal ? t('conversation.translation.show') : translation.languageName}
              </button>
            )}
            {translation.status === 'failed' &&
              (translation.reason ? (
                <span className="rl-translation-mark">{translation.reason}</span>
              ) : (
                <button
                  type="button"
                  className="rl-translation-mark"
                  onClick={() => translations.retry(message)}
                >
                  {t('conversation.translation.failed')}
                </button>
              ))}
          </div>
        )}

        {message.attachments.map((f, i) => (
          <AttachmentView key={f.nodeId} file={f} auto={auto[i]!} card={<AttachmentCard file={f} />} />
        ))}

        <Reactions message={message} readOnly={readOnly} {...(meId !== undefined ? { meId } : {})} />

        {message.thread && onThread && (
          <ThreadRow summary={message.thread} faces={faces ?? []} onOpen={() => onThread(message)} />
        )}
      </div>

      {!readOnly && !editing && (
        <span className="rl-msgtools">
          {QUICK.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="rl-reaction"
              title={t('conversation.react')}
              onClick={() => store.react?.(message.conversationId, message.id, emoji, true)}
            >
              {emoji}
            </button>
          ))}
          <IconButton
            icon="Reply"
            size="sm"
            label={t('conversation.reply')}
            onClick={() => onReply(message)}
          />
          <IconButton icon="Overflow" size="sm" label={t('common.more')} onClick={openFrom} />
        </span>
      )}

      {menu && (
        <MessageMenu entries={entries} at={menu} onPick={pick} onClose={() => setMenu(null)} />
      )}
    </div>
  );
}
